// Who is who: rows keep a user's id (`created_by`, an order's `server_id`), and names
// live on the server. They are fetched when online and kept on the device, so the
// Activity page and the floor can name people offline too. Unknown ids have no name.
import type { MemberRole } from '@ims/remote'
import { useEffect, useMemo, useState } from 'react'
import { useOnline } from '../hooks.ts'
import { useSession } from '../session.tsx'
import { roleAtLeast, useWorkspace } from '../workspace.tsx'

/** A member as this device last saw them. `role` is null in names kept by an older version. */
export interface StaffMember {
  id: string
  name: string
  role: MemberRole | null
}

// v3 keeps each member's role (for choosing an order's server); v2 kept names only
// and is still read until the first fetch. v1 cut names to their first word.
const key = (tenantId: string) => `ims.staff.v3.${tenantId}`
const oldKey = (tenantId: string) => `ims.staff.v2.${tenantId}`

function read(tenantId: string): Record<string, StaffMember> {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key(tenantId)) ?? 'null')
    if (value && typeof value === 'object') return value as Record<string, StaffMember>
    const names: unknown = JSON.parse(localStorage.getItem(oldKey(tenantId)) ?? '{}')
    if (!names || typeof names !== 'object') return {}
    return Object.fromEntries(
      Object.entries(names as Record<string, string>).map(([id, name]) => [
        id,
        { id, name, role: null },
      ]),
    )
  } catch {
    return {}
  }
}

/**
 * A person as the Activity page names them: their full name ("Wim Waiter"), cut to its
 * first two words when it is long; with no name, the part of the email before the @.
 * Never the first word alone: an owner called "Restaurant Demo" read as "Restaurant".
 */
export function shortName(fullName: string | null, email: string | null): string | null {
  const words = fullName?.trim().split(/\s+/).filter(Boolean) ?? []
  if (words.length > 0) {
    const full = words.join(' ')
    return full.length <= 22 ? full : words.slice(0, 2).join(' ')
  }
  const local = email?.split('@')[0]
  return local ? local : null
}

/** "Sita Rai" → "SR", "maya" → "MA": the letters in a server's circle on the floor. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase()
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase()
}

/** The business's members by user id, as this device last fetched them. */
function useDirectory(): Record<string, StaffMember> {
  const { current } = useWorkspace()
  const { platform } = useSession()
  const online = useOnline()
  const tenantId = current?.tenant_id ?? ''
  const [members, setMembers] = useState(() => read(tenantId))
  useEffect(() => {
    if (!tenantId || !online) return
    let active = true
    void (async () => {
      const { data: rows } = await platform.supabase
        .from('memberships')
        .select('user_id, role')
        .eq('tenant_id', tenantId)
      const roles = new Map((rows ?? []).map((m) => [m.user_id as string, m.role as MemberRole]))
      if (roles.size === 0) return
      const { data: profiles } = await platform.supabase
        .from('profiles')
        .select('id, email, full_name')
        .in('id', [...roles.keys()])
      const next: Record<string, StaffMember> = {}
      for (const p of profiles ?? []) {
        const id = p.id as string
        const name = shortName(p.full_name as string | null, p.email as string | null)
        if (name) next[id] = { id, name, role: roles.get(id) ?? null }
      }
      if (!active || Object.keys(next).length === 0) return
      try {
        localStorage.setItem(key(tenantId), JSON.stringify(next))
      } catch {
        // Storage blocked: the names still show for this session.
      }
      setMembers(next)
    })().catch(() => {})
    return () => {
      active = false
    }
  }, [tenantId, online, platform])
  return members
}

/** The business's members by user id, as short names. */
export function useStaffNames(): Record<string, string> {
  const members = useDirectory()
  return useMemo(
    () => Object.fromEntries(Object.values(members).map((m) => [m.id, m.name])),
    [members],
  )
}

/**
 * Who can look after an order (waiter and above), by name. A member whose role this
 * device has not seen yet is listed too: the server has the last word.
 */
export function useServers(): StaffMember[] {
  const members = useDirectory()
  return useMemo(
    () =>
      Object.values(members)
        .filter((m) => m.role === null || roleAtLeast(m.role, 'waiter'))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members],
  )
}
